import { useState, useMemo } from "react";
import { showSuccess, showError } from "@/lib/toastHelpers";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Package, Wrench, Hammer, ChefHat, Plus, Pencil, Trash2, Eye } from "lucide-react";

import { Pagination } from "@/components/Pagination";

interface Category {
  _id: string;
  id?: string;
  name: string;
  notes?: string;
}

interface StarterCategoriesTabProps {
  starterIngredientCategories: Category[];
  starterMaterialCategories: Category[];
  starterEquipmentCategories: Category[];
  starterRecipeCategories: Category[];
  itemsPerPage: number;
  setItemsPerPage: (size: number) => void;
  createIngredientCategoryMutation: any;
  updateIngredientCategoryMutation: any;
  deleteIngredientCategoryMutation: any;
  createMaterialCategoryMutation: any;
  updateMaterialCategoryMutation: any;
  deleteMaterialCategoryMutation: any;
  createEquipmentCategoryMutation: any;
  updateEquipmentCategoryMutation: any;
  deleteEquipmentCategoryMutation: any;
  createRecipeCategoryMutation: any;
  updateRecipeCategoryMutation: any;
  deleteRecipeCategoryMutation: any;
}

export function StarterCategoriesTab({
  starterIngredientCategories,
  starterMaterialCategories,
  starterEquipmentCategories,
  starterRecipeCategories,
  itemsPerPage,
  setItemsPerPage,
  createIngredientCategoryMutation,
  updateIngredientCategoryMutation,
  deleteIngredientCategoryMutation,
  createMaterialCategoryMutation,
  updateMaterialCategoryMutation,
  deleteMaterialCategoryMutation,
  createEquipmentCategoryMutation,
  updateEquipmentCategoryMutation,
  deleteEquipmentCategoryMutation,
  createRecipeCategoryMutation,
  updateRecipeCategoryMutation,
  deleteRecipeCategoryMutation,
}: StarterCategoriesTabProps) {

  // Pagination state
  const [ingredientPage, setIngredientPage] = useState(1);
  const [materialPage, setMaterialPage] = useState(1);
  const [equipmentPage, setEquipmentPage] = useState(1);
  const [recipePage, setRecipePage] = useState(1);

  // Delete confirmation state
  const [deleteCategory, setDeleteCategory] = useState<{ id: string; name: string; type: string } | null>(null);

  // Ingredient category state
  const [ingredientCategoryDialogOpen, setIngredientCategoryDialogOpen] = useState(false);
  const [editingIngredientCategory, setEditingIngredientCategory] = useState<Category | null>(null);
  const [ingredientCategoryName, setIngredientCategoryName] = useState("");
  const [ingredientCategoryNotes, setIngredientCategoryNotes] = useState("");

  // Material category state
  const [materialCategoryDialogOpen, setMaterialCategoryDialogOpen] = useState(false);
  const [editingMaterialCategory, setEditingMaterialCategory] = useState<Category | null>(null);
  const [materialCategoryName, setMaterialCategoryName] = useState("");
  const [materialCategoryNotes, setMaterialCategoryNotes] = useState("");

  // Equipment category state
  const [equipmentCategoryDialogOpen, setEquipmentCategoryDialogOpen] = useState(false);
  const [editingEquipmentCategory, setEditingEquipmentCategory] = useState<Category | null>(null);
  const [equipmentCategoryName, setEquipmentCategoryName] = useState("");
  const [equipmentCategoryNotes, setEquipmentCategoryNotes] = useState("");

  // Recipe category state
  const [recipeCategoryDialogOpen, setRecipeCategoryDialogOpen] = useState(false);
  const [editingRecipeCategory, setEditingRecipeCategory] = useState<Category | null>(null);
  const [recipeCategoryName, setRecipeCategoryName] = useState("");
  const [recipeCategoryNotes, setRecipeCategoryNotes] = useState("");

  const handleDeleteCategory = () => {
    if (!deleteCategory) return;

    const mutations = {
      ingredient: deleteIngredientCategoryMutation,
      material: deleteMaterialCategoryMutation,
      equipment: deleteEquipmentCategoryMutation,
      recipe: deleteRecipeCategoryMutation,
    };

    mutations[deleteCategory.type as keyof typeof mutations]?.mutate({ id: deleteCategory.id });
    setDeleteCategory(null);
  };

  const handleCreateIngredientCategory = (data: { name: string; notes: string }) => {
    createIngredientCategoryMutation.mutate(data, {
      onSuccess: () => {
        setIngredientCategoryDialogOpen(false);
        setIngredientCategoryName("");
        setIngredientCategoryNotes("");
      },
    });
  };

  const handleUpdateIngredientCategory = (id: string, data: { name: string; notes: string }) => {
    updateIngredientCategoryMutation.mutate({ id, data }, {
      onSuccess: () => {
        setIngredientCategoryDialogOpen(false);
        setEditingIngredientCategory(null);
      },
    });
  };

  const handleCreateMaterialCategory = (data: { name: string; notes: string }) => {
    createMaterialCategoryMutation.mutate(data, {
      onSuccess: () => {
        setMaterialCategoryDialogOpen(false);
        setMaterialCategoryName("");
        setMaterialCategoryNotes("");
      },
    });
  };

  const handleUpdateMaterialCategory = (id: string, data: { name: string; notes: string }) => {
    updateMaterialCategoryMutation.mutate({ id, data }, {
      onSuccess: () => {
        setMaterialCategoryDialogOpen(false);
        setEditingMaterialCategory(null);
      },
    });
  };

  const handleCreateEquipmentCategory = (data: { name: string; notes: string }) => {
    createEquipmentCategoryMutation.mutate(data, {
      onSuccess: () => {
        setEquipmentCategoryDialogOpen(false);
        setEquipmentCategoryName("");
        setEquipmentCategoryNotes("");
      },
    });
  };

  const handleUpdateEquipmentCategory = (id: string, data: { name: string; notes: string }) => {
    updateEquipmentCategoryMutation.mutate({ id, data }, {
      onSuccess: () => {
        setEquipmentCategoryDialogOpen(false);
        setEditingEquipmentCategory(null);
      },
    });
  };

  const handleCreateRecipeCategory = (data: { name: string; notes: string }) => {
    createRecipeCategoryMutation.mutate(data, {
      onSuccess: () => {
        setRecipeCategoryDialogOpen(false);
        setRecipeCategoryName("");
        setRecipeCategoryNotes("");
      },
    });
  };

  const handleUpdateRecipeCategory = (id: string, data: { name: string; notes: string }) => {
    updateRecipeCategoryMutation.mutate({ id, data }, {
      onSuccess: () => {
        setRecipeCategoryDialogOpen(false);
        setEditingRecipeCategory(null);
      },
    });
  };

  // Paginated categories
  const paginatedIngredientCategories = useMemo(() => {
    const start = (ingredientPage - 1) * itemsPerPage;
    return starterIngredientCategories?.slice(start, start + itemsPerPage) || [];
  }, [starterIngredientCategories, ingredientPage]);

  const paginatedMaterialCategories = useMemo(() => {
    const start = (materialPage - 1) * itemsPerPage;
    return starterMaterialCategories?.slice(start, start + itemsPerPage) || [];
  }, [starterMaterialCategories, materialPage]);

  const paginatedEquipmentCategories = useMemo(() => {
    const start = (equipmentPage - 1) * itemsPerPage;
    return starterEquipmentCategories?.slice(start, start + itemsPerPage) || [];
  }, [starterEquipmentCategories, equipmentPage]);

  const paginatedRecipeCategories = useMemo(() => {
    const start = (recipePage - 1) * itemsPerPage;
    return starterRecipeCategories?.slice(start, start + itemsPerPage) || [];
  }, [starterRecipeCategories, recipePage]);

  const ingredientTotalPages = Math.ceil((starterIngredientCategories?.length || 0) / itemsPerPage);
  const materialTotalPages = Math.ceil((starterMaterialCategories?.length || 0) / itemsPerPage);
  const equipmentTotalPages = Math.ceil((starterEquipmentCategories?.length || 0) / itemsPerPage);
  const recipeTotalPages = Math.ceil((starterRecipeCategories?.length || 0) / itemsPerPage);

  return (
    <div className="grid gap-6 grid-cols-1 xl:grid-cols-3">
      {/* Ingredient Categories */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Ingredient Categories
            </CardTitle>
            <CardDescription>Categories for starter ingredients</CardDescription>
          </div>
          <Dialog open={ingredientCategoryDialogOpen} onOpenChange={(open) => {
            setIngredientCategoryDialogOpen(open);
            if (!open) {
              setEditingIngredientCategory(null);
              setIngredientCategoryName("");
              setIngredientCategoryNotes("");
            }
          }}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>{editingIngredientCategory ? "Edit" : "Add"} Ingredient Category</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Category Name *</Label>
                  <Input
                    value={ingredientCategoryName}
                    onChange={(e) => setIngredientCategoryName(e.target.value)}
                    placeholder="e.g., Baking Essentials"
                  />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Input
                    value={ingredientCategoryNotes}
                    onChange={(e) => setIngredientCategoryNotes(e.target.value)}
                    placeholder="Optional notes"
                  />
                </div>
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button
                  onClick={() => {
                    if (!ingredientCategoryName.trim()) return;
                    if (editingIngredientCategory) {
                      handleUpdateIngredientCategory(editingIngredientCategory._id || editingIngredientCategory.id!, {
                        name: ingredientCategoryName.trim(),
                        notes: ingredientCategoryNotes.trim()
                      });
                    } else {
                      handleCreateIngredientCategory({
                        name: ingredientCategoryName.trim(),
                        notes: ingredientCategoryNotes.trim()
                      });
                    }
                  }}
                  disabled={!ingredientCategoryName.trim()}
                >
                  {editingIngredientCategory ? "Update" : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {paginatedIngredientCategories?.map((cat) => (
              <div key={cat._id} className="flex items-center justify-between p-2 hover:bg-accent rounded">
                <span className="text-sm">{cat.name}</span>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditingIngredientCategory(cat);
                      setIngredientCategoryName(cat.name);
                      setIngredientCategoryNotes(cat.notes || "");
                      setIngredientCategoryDialogOpen(true);
                    }}
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteCategory({ id: cat._id, name: cat.name, type: 'ingredient' })}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          {ingredientTotalPages > 1 && (
            <div className="mt-4">
              <Pagination
                currentPage={ingredientPage}
                totalPages={ingredientTotalPages}
                totalItems={starterIngredientCategories?.length || 0}
                itemsPerPage={itemsPerPage}
                onPageChange={setIngredientPage}
                onPageSizeChange={setItemsPerPage}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Material Categories */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              Material Categories
            </CardTitle>
            <CardDescription>Categories for starter materials</CardDescription>
          </div>
          <Dialog open={materialCategoryDialogOpen} onOpenChange={(open) => {
            setMaterialCategoryDialogOpen(open);
            if (!open) {
              setEditingMaterialCategory(null);
              setMaterialCategoryName("");
              setMaterialCategoryNotes("");
            }
          }}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>{editingMaterialCategory ? "Edit" : "Add"} Material Category</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Category Name *</Label>
                  <Input
                    value={materialCategoryName}
                    onChange={(e) => setMaterialCategoryName(e.target.value)}
                    placeholder="e.g., Packaging"
                  />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Input
                    value={materialCategoryNotes}
                    onChange={(e) => setMaterialCategoryNotes(e.target.value)}
                    placeholder="Optional notes"
                  />
                </div>
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button
                  onClick={() => {
                    if (!materialCategoryName.trim()) return;
                    if (editingMaterialCategory) {
                      handleUpdateMaterialCategory(editingMaterialCategory._id || editingMaterialCategory.id!, {
                        name: materialCategoryName.trim(),
                        notes: materialCategoryNotes.trim()
                      });
                    } else {
                      handleCreateMaterialCategory({
                        name: materialCategoryName.trim(),
                        notes: materialCategoryNotes.trim()
                      });
                    }
                  }}
                  disabled={!materialCategoryName.trim()}
                >
                  {editingMaterialCategory ? "Update" : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {paginatedMaterialCategories?.map((cat) => (
              <div key={cat._id} className="flex items-center justify-between p-2 hover:bg-accent rounded">
                <span className="text-sm">{cat.name}</span>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditingMaterialCategory(cat);
                      setMaterialCategoryName(cat.name);
                      setMaterialCategoryNotes(cat.notes || "");
                      setMaterialCategoryDialogOpen(true);
                    }}
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteCategory({ id: cat._id, name: cat.name, type: 'material' })}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          {materialTotalPages > 1 && (
            <div className="mt-4">
              <Pagination
                currentPage={materialPage}
                totalPages={materialTotalPages}
                totalItems={starterMaterialCategories?.length || 0}
                itemsPerPage={itemsPerPage}
                onPageChange={setMaterialPage}
                onPageSizeChange={setItemsPerPage}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Equipment Categories */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Hammer className="h-5 w-5" />
              Equipment Categories
            </CardTitle>
            <CardDescription>Categories for starter equipment</CardDescription>
          </div>
          <Dialog open={equipmentCategoryDialogOpen} onOpenChange={(open) => {
            setEquipmentCategoryDialogOpen(open);
            if (!open) {
              setEditingEquipmentCategory(null);
              setEquipmentCategoryName("");
              setEquipmentCategoryNotes("");
            }
          }}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>{editingEquipmentCategory ? "Edit" : "Add"} Equipment Category</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Category Name *</Label>
                  <Input
                    value={equipmentCategoryName}
                    onChange={(e) => setEquipmentCategoryName(e.target.value)}
                    placeholder="e.g., Kitchen Tools"
                  />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Input
                    value={equipmentCategoryNotes}
                    onChange={(e) => setEquipmentCategoryNotes(e.target.value)}
                    placeholder="Optional notes"
                  />
                </div>
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button
                  onClick={() => {
                    if (!equipmentCategoryName.trim()) return;
                    if (editingEquipmentCategory) {
                      handleUpdateEquipmentCategory(editingEquipmentCategory._id || editingEquipmentCategory.id!, {
                        name: equipmentCategoryName.trim(),
                        notes: equipmentCategoryNotes.trim()
                      });
                    } else {
                      handleCreateEquipmentCategory({
                        name: equipmentCategoryName.trim(),
                        notes: equipmentCategoryNotes.trim()
                      });
                    }
                  }}
                  disabled={!equipmentCategoryName.trim()}
                >
                  {editingEquipmentCategory ? "Update" : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {paginatedEquipmentCategories?.map((cat) => (
              <div key={cat._id} className="flex items-center justify-between p-2 hover:bg-accent rounded">
                <span className="text-sm">{cat.name}</span>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditingEquipmentCategory(cat);
                      setEquipmentCategoryName(cat.name);
                      setEquipmentCategoryNotes(cat.notes || "");
                      setEquipmentCategoryDialogOpen(true);
                    }}
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteCategory({ id: cat._id, name: cat.name, type: 'equipment' })}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          {equipmentTotalPages > 1 && (
            <div className="mt-4">
              <Pagination
                currentPage={equipmentPage}
                totalPages={equipmentTotalPages}
                totalItems={starterEquipmentCategories?.length || 0}
                itemsPerPage={itemsPerPage}
                onPageChange={setEquipmentPage}
                onPageSizeChange={setItemsPerPage}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recipe Categories */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ChefHat className="h-5 w-5" />
              Recipe Categories
            </CardTitle>
            <CardDescription>Categories for ChefAiQ recipes</CardDescription>
          </div>
          <Dialog open={recipeCategoryDialogOpen} onOpenChange={(open) => {
            setRecipeCategoryDialogOpen(open);
            if (!open) {
              setEditingRecipeCategory(null);
              setRecipeCategoryName("");
              setRecipeCategoryNotes("");
            }
          }}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>{editingRecipeCategory ? "Edit" : "Add"} Recipe Category</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Category Name *</Label>
                  <Input
                    value={recipeCategoryName}
                    onChange={(e) => setRecipeCategoryName(e.target.value)}
                    placeholder="e.g., Desserts"
                  />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Input
                    value={recipeCategoryNotes}
                    onChange={(e) => setRecipeCategoryNotes(e.target.value)}
                    placeholder="Optional notes"
                  />
                </div>
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button
                  onClick={() => {
                    if (!recipeCategoryName.trim()) return;
                    if (editingRecipeCategory) {
                      handleUpdateRecipeCategory(editingRecipeCategory._id || editingRecipeCategory.id!, {
                        name: recipeCategoryName.trim(),
                        notes: recipeCategoryNotes.trim()
                      });
                    } else {
                      handleCreateRecipeCategory({
                        name: recipeCategoryName.trim(),
                        notes: recipeCategoryNotes.trim()
                      });
                    }
                  }}
                  disabled={!recipeCategoryName.trim()}
                >
                  {editingRecipeCategory ? "Update" : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {paginatedRecipeCategories?.map((cat) => (
              <div key={cat._id} className="flex items-center justify-between p-2 hover:bg-accent rounded">
                <span className="text-sm">{cat.name}</span>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditingRecipeCategory(cat);
                      setRecipeCategoryName(cat.name);
                      setRecipeCategoryNotes(cat.notes || "");
                      setRecipeCategoryDialogOpen(true);
                    }}
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteCategory({ id: cat._id, name: cat.name, type: 'recipe' })}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          {recipeTotalPages > 1 && (
            <div className="mt-4">
              <Pagination
                currentPage={recipePage}
                totalPages={recipeTotalPages}
                totalItems={starterRecipeCategories?.length || 0}
                itemsPerPage={itemsPerPage}
                onPageChange={setRecipePage}
                onPageSizeChange={setItemsPerPage}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteCategory} onOpenChange={() => setDeleteCategory(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteCategory?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCategory}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
